/** Campanii (App-Campanii.dc.html) — filtre Toate/Active/Arhivate, context comun, „+ Clip în campanie". */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaigns, useMutation, useInvalidate, qk } from '../api/hooks';
import { createCampaign } from '../api/endpoints';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { categoryLabel } from '../lib/format';
import { EmptyState, Modal, PageHeader, Segmented, SkeletonCard, Spinner } from '../components/ui';

type Filter = 'toate' | 'activa' | 'arhivata';

export function CampaignsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('toate');
  const [addOpen, setAddOpen] = useState(false);
  const campaigns = useCampaigns(filter === 'toate' ? undefined : filter);
  const invalidate = useInvalidate();
  const toast = useToast();

  useEffect(() => {
    document.title = 'Campanii — Reclame Video';
  }, []);

  const items = campaigns.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Campanii"
        subtitle="Grupezi clipurile cu același produs, ton și format — AI-ul păstrează continuitatea."
        actions={
          <>
            <Segmented<Filter>
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'toate', label: 'Toate' },
                { value: 'activa', label: 'Active' },
                { value: 'arhivata', label: 'Arhivate' },
              ]}
            />
            <button className="btn btn-primary btn-sm" data-testid="add-campaign" onClick={() => setAddOpen(true)}>+ Campanie</button>
          </>
        }
      />

      {campaigns.isLoading ? (
        <div className="grid-auto">{[0, 1].map((i) => <SkeletonCard key={i} />)}</div>
      ) : items.length === 0 ? (
        <EmptyState
          illustration="📣"
          title="Nicio campanie încă"
          body="Creează o campanie ca să grupezi clipurile cu același produs și ton."
          cta={<button className="btn btn-primary" onClick={() => setAddOpen(true)}>Campanie nouă</button>}
        />
      ) : (
        <div className="grid-auto" data-testid="campaigns-grid">
          {items.map((c) => (
            <div key={c.id} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 800, fontSize: 16 }}>{c.name}</span>
                <span className="pill" style={{ marginLeft: 'auto', background: c.status === 'activa' ? 'var(--ok-soft)' : 'var(--bg2)', color: c.status === 'activa' ? 'var(--ok)' : 'var(--muted)' }}>
                  {c.status === 'activa' ? 'Activă' : 'Arhivată'}
                </span>
              </div>
              {c.category && <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{categoryLabel(c.category)}</span>}
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/creeaza')} style={{ marginTop: 'auto', alignSelf: 'flex-start' }}>+ Clip în campanie</button>
            </div>
          ))}
        </div>
      )}

      <AddCampaignModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => { invalidate([qk.campaigns()]); setAddOpen(false); toast.push({ kind: 'ok', title: 'Campanie creată' }); }}
      />
    </div>
  );
}

function AddCampaignModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<'organic' | 'reclame'>('organic');
  const toast = useToast();
  const mut = useMutation({
    mutationFn: () => createCampaign({ name, category }),
    onSuccess: onCreated,
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });
  return (
    <Modal open={open} onClose={onClose} title="Campanie nouă">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label className="label">Nume<input className="input" data-testid="campaign-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="chip" data-on={category === 'organic'} onClick={() => setCategory('organic')}>Organic</button>
          <button className="chip" data-on={category === 'reclame'} onClick={() => setCategory('reclame')}>Reclame</button>
        </div>
        <button className="btn btn-primary" data-testid="save-campaign" onClick={() => mut.mutate()} disabled={mut.isPending || name.trim().length < 2}>
          {mut.isPending ? <Spinner /> : 'Creează campania'}
        </button>
      </div>
    </Modal>
  );
}
